/** Row from GET /api/ai/models — a model the gateway's Anthropic key can call. */
export interface LiveModel {
  id: string;
  displayName: string;
  createdAt: string;
}

export interface ModelOption {
  value: string;
  label: string;
  description?: string;
}

export interface ModelGroup {
  label: string;
  options: ModelOption[];
}

/**
 * The current lineup, in recommended order (first = fallback when the default
 * model is removed from the allow-list). Ids are the Anthropic Claude API ids;
 * every one needs a rate in api/src/lib/pricing.ts or its cost shows as $0.
 * Anything else the key can call is listed under "Previous generations" from
 * the live Models API, so a new model never needs a code change to be pickable.
 */
export const CURRENT_MODELS: ModelOption[] = [
  { value: 'claude-sonnet-5-5', label: 'Sonnet 5.5', description: 'Balanced speed and intelligence' },
  { value: 'claude-opus-5-5', label: 'Opus 5.5', description: 'Advanced reasoning' },
  { value: 'claude-haiku-5-5', label: 'Haiku 5.5', description: 'Fast and lowest cost' },
  { value: 'claude-fable-5-1', label: 'Fable 5.1', description: 'Most capable, highest cost' },
];

/** Readable names for ids a saved config may still hold when the live list is unavailable. */
const KNOWN_PREVIOUS: Record<string, string> = {
  'claude-haiku-4-5-20251001': 'Haiku 4.5',
  'claude-haiku-4-5': 'Haiku 4.5',
  'claude-sonnet-5': 'Sonnet 5',
  'claude-sonnet-4-6': 'Sonnet 4.6',
  'claude-opus-5': 'Opus 5',
  'claude-opus-4-8': 'Opus 4.8',
  'claude-fable-5': 'Fable 5',
};

const isCurrent = (id: string) => CURRENT_MODELS.some((m) => m.value === id);

/** Label for any model id: catalog name, then the API's display name, then the raw id. */
export function labelFor(id: string, live: LiveModel[] | null): string {
  const current = CURRENT_MODELS.find((m) => m.value === id);
  if (current) return current.label;
  const name = live?.find((m) => m.id === id)?.displayName ?? KNOWN_PREVIOUS[id];
  return name ? `${name} (previous)` : id;
}

/**
 * The picker's option groups. With the live list, only models the key can call
 * are offered. Without it (the Models API failed) fall back to the static
 * catalog so the page still works — the API re-checks on save when it can.
 */
export function buildModelGroups(live: LiveModel[] | null): ModelGroup[] {
  const liveIds = live ? new Set(live.map((m) => m.id)) : null;
  const current = CURRENT_MODELS.filter((m) => !liveIds || liveIds.has(m.value));
  const previous: ModelOption[] = live
    ? [...live]
        .filter((m) => !isCurrent(m.id))
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        .map((m) => ({ value: m.id, label: labelFor(m.id, live) }))
    : Object.keys(KNOWN_PREVIOUS)
        .filter((id) => id !== 'claude-haiku-4-5')
        .map((id) => ({ value: id, label: labelFor(id, null) }));
  return [
    { label: 'Current', options: current },
    ...(previous.length ? [{ label: 'Previous generations', options: previous }] : []),
  ].filter((g) => g.options.length > 0);
}

/** Selected ids the key cannot call (empty when the live list is unavailable). */
export function unavailableModels(selected: readonly string[], live: LiveModel[] | null): string[] {
  if (!live) return [];
  const ids = new Set(live.map((m) => m.id));
  return selected.filter((id) => !ids.has(id));
}

/** Catalog order (recommended first), then anything else — the default to fall back on. */
export function preferredDefault(allowed: readonly string[]): string | undefined {
  return CURRENT_MODELS.find((m) => allowed.includes(m.value))?.value ?? allowed[0];
}
